import { ToastrService } from 'ngx-toastr';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { IFsLprQueryParameters } from '../interfaces/lpr-query-parameters.interface';
import { LprQueryModel, LprQueryResultsModel } from '../models/lpr-query.model';

@Injectable({ providedIn: 'root' })
export class FsLprQueryService {
  private _api: string;

  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
  ) {
    this._api = localStorage.getItem('LPR_API') ?? '';
  }

  public getOrganizationQueries(params?: any): Observable<any> {
    if (!this._api) {
      console.warn('LPR API is disabled or not configured');
      return of({
        count: 0,
        totalPages: 0,
        results: [],
      });
    }
    return this._httpClient.get(`${this._api}/api/query/organization/`, { params }).pipe(
      map((response: any) => ({
        count: response.count || response.items.length,
        totalPages: response.size,
        nextToken: response.next_token,
        results: response.items.map((q) => new LprQueryModel(q)),
      })),
      tap({
        error: () => this._toastrService.error('Unable to load queries. Please try again later'),
      }),
    );
  }

  public getMyQueries(params?: any): Observable<any> {
    if (!this._api) {
      console.warn('LPR API is disabled or not configured');
      return of({
        count: 0,
        totalPages: 0,
        results: [],
      });
    }
    return this._httpClient.get(`${this._api}/api/query/my/`, { params }).pipe(
      map((response: any) => ({
        count: response.count || response.items.length,
        totalPages: response.size,
        nextToken: response.next_token,
        results: response.items.map((q) => new LprQueryModel(q)),
      })),
      tap({
        error: () => this._toastrService.error('Unable to load queries. Please try again later'),
      }),
    );
  }

  public getTotalQueryResultsCount(params?: any): Observable<any> {
    if (!this._api) {
      console.warn('LPR API is disabled or not configured');
      return of({
        total: 0,
      });
    }
    return this._httpClient.get(`${this._api}/api/reads/count/`, { params });
  }

  public getQueryResults(queryId: string, params?: IFsLprQueryParameters): Observable<any> {
    if (!this._api) {
      console.warn('LPR API is disabled or not configured');
      return of({
        count: 0,
        totalPages: 0,
        results: [],
      });
    }
    return this._httpClient.get(`${this._api}/api/query/${queryId}/`, { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.size,
        cursor: response.next_token,
        results: response.items.map((query) => new LprQueryResultsModel(query)),
      })),
      tap({
        error: () =>
          this._toastrService.error('Unable to load query results. Please try again later'),
      }),
    );
  }

  public getQuery(queryId: string, params?: IFsLprQueryParameters): Observable<any> {
    if (!this._api) {
      console.warn('LPR API is disabled or not configured');
      return of({});
    }
    return this._httpClient.get(`${this._api}/api/query/${queryId}/data/`, { params });
  }
}
